import React, { useMemo, useState } from "react";

import { UNKNOWN_BALANCE } from "@fartherfinance/frontend/api/Accounts/utilities/accountUtil";
import useGetItbs from "@fartherfinance/frontend/api/Investment/hooks/useGetItbs";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { Column, Row } from "@src/sharedComponents/Table/Types";

const UserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EasternTimeZone = "America/New_York";

const UseEastern = true;

const timeSuffix = UseEastern ? " (East)" : " (Local)";

type Keys =
  | "batchId"
  | "expireTime"
  | "minimumAccountFreshness"
  | "assignments"
  | "assignment1"
  | "assignment2";

const columns: Column<Keys>[] = [
  { accessor: "batchId", label: "Batch Id", sortable: false },
  { accessor: "expireTime", label: "Expire Time", sortable: false },
  {
    accessor: "minimumAccountFreshness",
    label: "Min Fresh",
    sortable: false,
  },
  { accessor: "assignments", label: "Assignments", sortable: false },
  { accessor: "assignment1", label: "Assignment 1", sortable: false },
  { accessor: "assignment2", label: "Assignment 2", sortable: false },
];

const ItbsTable: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(20);

  const auth = useAdvisorRequestAuth();
  const itbs = useGetItbs({ page, pageSize, orderBy: "createdAt desc" }, auth);

  const data = useMemo((): Row<Keys, string>[] => {
    if (itbs.data === undefined) {
      return [];
    }

    return itbs.data.data.map((itb) => ({
      key: itb.batchId,
      batchId: itb.batchId,
      minimumAccountFreshness: `${itb.minimumAccountFreshness.toLocaleString(
        "en-US",
        {
          timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
          dateStyle: "short",
          timeStyle: "short",
        }
      )}${timeSuffix}`,
      expireTime:
        itb.expireTime === null
          ? UNKNOWN_BALANCE
          : `${itb.expireTime.toLocaleString("en-US", {
              timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
              dateStyle: "short",
              timeStyle: "short",
            })}${timeSuffix}`,
      assignments: itb.assignments.length.toString(),
      assignment1:
        itb.assignments[0] === undefined
          ? ""
          : `${itb.assignments[0].portfolioPlanAssignmentId} (v${itb.assignments[0].PortfolioPlanAssignmentVersion})`,
      assignment2:
        itb.assignments[1] === undefined
          ? ""
          : `${itb.assignments[1].portfolioPlanAssignmentId} (v${itb.assignments[1].PortfolioPlanAssignmentVersion})`,
    }));
  }, [itbs.data]);

  if (itbs.isLoading || itbs.hasError) {
    return (
      <PaginatedTable
        isLoading={true}
        columns={columns}
        rows={[]}
        pagination={{
          currentPage: page,
          totalPages: 1,
          pageSize: pageSize,
          totalCount: 0,
        }}
        onPageChange={() => {}}
      />
    );
  }

  const totalPages = Math.ceil(
    itbs.data.pagination.totalCount / itbs.data.pagination.pageSize
  );

  return (
    <PaginatedTable
      isLoading={false}
      columns={columns}
      rows={data}
      pagination={{
        currentPage: page,
        totalPages: totalPages,
        pageSize: itbs.data.pagination.pageSize,
        totalCount: itbs.data.pagination.totalCount,
        prevPage: page === 1 ? undefined : page - 1,
        nextPage: page === totalPages ? undefined : page + 1,
      }}
      onPageChange={(newPage) => {
        setPage(newPage);
      }}
    />
  );
};

export default ItbsTable;
